/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import { Add, Delete, Edit, Lock, MoreVert } from "@mui/icons-material";
import {
  alpha,
  Box,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  Paper,
  Popper,
  styled,
  SxProps,
  Table as MuiTable,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  PimoClassComponent,
  PimoReactComponent,
  TableDefinition,
} from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Tooltip } from "../../molecules";
import {
  PMTableEventNames,
  PMTableEventPayload,
  PMTableProps,
  PMTableRowProps,
  TableHeaderEntry,
} from "../../types/pm-table";
import PMBaseCard from "../pm-base-card/pm-base-card";
import { PMDraggableTableCell } from "./pm-draggable-table-cell";

/**
 * Fixed width (px) of the row-menu column rendered when a row has
 * `onMenuOpen`. Shared between the real `<TableCell>` render (header + body)
 * and the synthetic sticky-column candidate used to detect/position the
 * sticky-column overlap shadow, so the two can never drift apart.
 */
const MENU_COLUMN_WIDTH_PX = 36;

export const PMTableRow = styled(TableRow)(({ theme }) => ({
  borderBottom: "1px solid",
  borderBottomColor: theme.palette.secondary.main,
}));
export const PMTableHeader = styled(TableHead)(({ theme }) => ({
  borderBottom: "1px solid",
  borderTop: "1px solid",
  borderColor: theme.palette.secondary.main,
  borderRadius: "0px",
}));
export const PMTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "1px solid",
  borderBottomColor: theme.palette.secondary.main,
  maxHeight: "55px",
}));

/**
 * Renders the sticky-column overlap shadow as a single, continuous overlay
 * spanning the full table height (header + all rows), instead of one small
 * `box-shadow` per cell.
 *
 * Defined at module scope (not inside `PMTable`'s render closure): a
 * component defined *inside* another component's render function is a new
 * function/type identity on every render, which forces React to unmount and
 * remount its whole subtree on every re-render (e.g. every scroll event
 * updating `isVisible`) instead of just updating it in place — breaking both
 * the `box-shadow` transition and any external DOM references to it.
 *
 * Two constraints drive this shape:
 *  - MUI's `<Table>` uses `border-collapse: collapse`, so browsers never
 *    paint `box-shadow` on table cells (`<td>`/`<th>`) themselves — it must
 *    be a non-table-cell descendant instead.
 *  - A `box-shadow` applied per-cell (once per row) produces a visibly
 *    segmented/scalloped shadow: the shadow's blur radius fades out at the
 *    top and bottom of every single cell's box, creating gaps at each row
 *    boundary instead of one continuous line.
 *
 * The fix renders a `position: sticky` marker (tracking the same horizontal
 * offset as the sticky column, with `height: 0` so it takes no space in the
 * flow) as a child of the scrolling `TableContainer`, placed before the
 * `<table>` so its static vertical position is the very top of the table.
 * `position: sticky` establishes a containing block for absolutely-
 * positioned descendants (same as `position: relative` would), so a single
 * absolutely-positioned child inside it can stretch to the table's full
 * measured height and paint one continuous shadow.
 *
 * The marker always positions itself via an explicit pixel `left` offset —
 * never via the CSS `right` property — even for the right-sticky shadow.
 * This is because the marker is a plain block-level sibling of `<table>`,
 * not itself a table cell: its *static* (pre-sticky) position sits at the
 * far left of the wide scrollable content (like any ordinary block element),
 * unlike a real right-sticky table cell whose static position is already
 * near the right edge (it's literally one of the last columns). Per CSS's
 * inset-resolution rules, a statically-positioned box with `left: auto` and
 * only `right` set is shifted *further left* by the `right` value (not
 * anchored to the container's right edge), which put the overlay
 * completely off-screen when this used `right` directly. Resolving an
 * explicit `left` in pixels (from the measured table width) sidesteps that
 * entirely, matching the left-sticky marker's already-correct behavior.
 */
const StickyColumnShadowOverlay = ({
  side,
  leftPx,
  widthPx,
  isVisible,
  heightPx,
  transition,
  shadowColor,
}: {
  side: "left" | "right";
  leftPx: number;
  widthPx: number;
  isVisible: boolean;
  heightPx: number;
  transition: string;
  shadowColor: string;
}) => {
  const boxShadow = isVisible
    ? side === "left"
      ? `4px 0 6px -4px ${shadowColor}`
      : `-4px 0 6px -4px ${shadowColor}`
    : "none";

  return (
    <Box
      aria-hidden
      data-testid={`sticky-shadow-overlay-${side}`}
      sx={{
        position: "sticky",
        left: leftPx,
        width: widthPx,
        height: 0,
        zIndex: 6,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: heightPx,
          boxShadow,
          transition,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

/**
 * A flexible table component that renders:
 *  - An optional left-aligned icon and title
 *  - Optional right-aligned info and menu buttons
 *  - Customizable cells for different datatypes
 *
 * @param props.icon               Icon node or string key to resolve from MUI Icons.
 * @param props.title              Title text or custom node.
 * @param props.showInfoIcon       Whether to display the info button. @default true
 * @param props.infoIcon           Icon node or string key for the info button. @default "InfoOutlined"
 * @param props.showMenuIcon       Whether to display the menu button. @default true
 * @param props.menuIcon           Icon node or string key for the menu button. @default "MoreVert"
 * @param props.fixedWidth         Width of the card; numbers are in px. @default "300px"
 * @param props.fixedHeight        Height of the card; numbers are in px. @default "150px"
 * @param props.rightSlotOverride  An override for the right-hand side slot of the title bar.
 * @param props.isStickyHeader     Flag indicating that the header of the table card should be sticky.
 * @param props.hideRightStickyShadow Whether to hide the right sticky-column shadow. @default false
 * @param props.allowColumnReorder Allows the columns of the table to be reordered using drag & drop.
 * @param props.tableHeaderEntries The table header configuration object.
 * @param props.data               The actual data of the table.
 * @returns A MUI Card component with a customizable MUI Table.
 */
class PMTable<Definition extends TableDefinition> extends PimoClassComponent<
  any,
  PMTableEventNames,
  PMTableEventPayload
> {
  constructor(private componentsInColumns: Definition) {
    super();
  }

  render(): PimoReactComponent<any, PMTableEventNames, PMTableEventPayload> {
    return ({
      title,
      icon,
      showInfoIcon,
      showMenuIcon,
      rightSlotOverride,
      bottomRightSlotOverride,
      data,
      tableHeaderEntries,
      isStickyHeader = false,
      hideRightStickyShadow = false,
      allowColumnReorder = false,
      colOrder,
      setColOrder,
      fixedWidth = "100%",
      fixedHeight = "100%",
      overflow,
      language = "en",
      isEditMode = false,
      fireEvent,
      tableContainerProps,
      tableSx,
    }: {
      data: {
        columnProps: PMTable<Definition>["componentsInColumns"];
        rowProps: PMTableRowProps;
      }[];
    } & PMTableProps) => {
      // Sticky Header Logic

      const theme = useTheme();
      const tableContainerRef = useRef<HTMLDivElement>(null);
      // The actual horizontally-scrolling element is the MUI TableContainer
      // rendered below (overflow: "auto"), not the outer maxHeight-clamping
      // Box that `tableContainerRef` is attached to (overflow: "hidden").
      // Scroll-position tracking for the sticky-column overlap shadow must
      // observe this element specifically.
      const tableScrollContainerRef = useRef<HTMLDivElement>(null);
      const [maxHeight, setMaxHeight] = useState<string | undefined>(undefined);
      const [currentColumnOnFocus, setCurrentColumnOnFocus] = useState<
        number | null
      >(null);
      const [isScrolledFromStart, setIsScrolledFromStart] = useState(false);
      const [isScrolledFromEnd, setIsScrolledFromEnd] = useState(false);
      // Height (px) of the actual <table> element, kept in sync via a
      // ResizeObserver. Used to size the continuous sticky-column overlap
      // shadow overlays so they span the full table (header + all rows)
      // rather than being drawn per-cell (see `stickyColumns` below for why).
      const tableElementRef = useRef<HTMLTableElement>(null);
      const [tableHeightPx, setTableHeightPx] = useState(0);
      // Visible width (px) of the scrolling `TableContainer` viewport itself
      // (its `clientWidth`, NOT the full/scrollable `<table>` content width).
      // Needed because right-sticky overlap-shadow overlays are positioned
      // via an explicit pixel `left` offset (derived from this) rather than
      // the CSS `right` property — see the comment on
      // `StickyColumnShadowOverlay`'s right-side offset computation below
      // for why `right` alone doesn't work for a plain sibling marker
      // element, and why this must be the *viewport's* width, not the
      // table's: the marker's stuck viewport position must match where the
      // real right-sticky `<th>`/`<td>` cell sticks, which is measured from
      // the scrollport's own edge, not the (much wider) scrollable content.
      const [containerWidthPx, setContainerWidthPx] = useState(0);
      useEffect(() => {
        if (!isStickyHeader) {
          return;
        }

        const updateHeight = () => {
          const viewportHeight = window.innerHeight;
          const offsetTop = tableContainerRef.current?.offsetTop || 0;
          const marginBottom = 40;

          setMaxHeight(`${viewportHeight - offsetTop - marginBottom}px`);
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
      }, [isStickyHeader]);

      useEffect(() => {
        const scrollEl = tableScrollContainerRef.current;

        if (!scrollEl) {
          return;
        }

        const updateScrollState = () => {
          setIsScrolledFromStart(scrollEl.scrollLeft > 0);
          // 1px tolerance for sub-pixel rounding.
          setIsScrolledFromEnd(
            scrollEl.scrollLeft + scrollEl.clientWidth <
              scrollEl.scrollWidth - 1
          );
          setContainerWidthPx(scrollEl.clientWidth);
        };

        updateScrollState();
        scrollEl.addEventListener("scroll", updateScrollState);
        window.addEventListener("resize", updateScrollState);

        return () => {
          scrollEl.removeEventListener("scroll", updateScrollState);
          window.removeEventListener("resize", updateScrollState);
        };
        // Re-evaluate whenever the rendered columns/rows (and therefore the
        // scrollWidth of the table) might have changed.
      }, [data, tableHeaderEntries]);

      const stickyShadowTransition = theme.transitions.create("box-shadow", {
        duration: theme.transitions.duration.shortest,
      });

      /** Normalizes an `SxProps` value into a plain record, when possible (i.e. not a function/array). */
      const asSxRecord = (
        sx: SxProps | undefined
      ): Record<string, unknown> | undefined =>
        sx && typeof sx === "object" && !Array.isArray(sx)
          ? (sx as Record<string, unknown>)
          : undefined;

      /**
       * Default width (px) a header cell falls back to when its own `sx`
       * doesn't declare one — must match the `minWidth: 150, width: 150`
       * default applied to header cells further below, since that's what
       * actually determines the rendered column width for `table-layout:
       * fixed` tables (the header row is the authoritative source for
       * column widths under that layout mode).
       */
      const DEFAULT_COLUMN_WIDTH_PX = 150;

      const getEffectiveColumnWidthPx = (
        sx: Record<string, unknown> | undefined
      ): number => {
        const declaredWidth = sx?.width ?? sx?.minWidth;
        const parsed = parseFloat(String(declaredWidth));
        return Number.isNaN(parsed) ? DEFAULT_COLUMN_WIDTH_PX : parsed;
      };
      const hasMenuColumn = Boolean(
        data?.some((row) => row.rowProps?.onMenuOpen)
      );

      // Draggable Column Logic
      const [tableData, setTableData] = useState({
        components: this.componentsInColumns,
        headers: tableHeaderEntries,
        data,
      });

      /**
       * Determines the single sticky column on each side (left/right) that is
       * actually adjacent to the horizontally-scrolling (non-sticky) columns —
       * i.e. the "innermost" sticky column of each group. Only that column
       * should show the overlap shadow: sticky columns stacked deeper (e.g. a
       * right-sticky "menu" column sitting outside a right-sticky "Review
       * Status" column) never border scrollable content directly, so drawing
       * a shadow there would be a spurious extra divider between two sticky
       * columns rather than a real scroll-overlap indicator.
       *
       * Determined from the declared `left`/`right` offsets alone: within a
       * contiguous sticky-left stack starting at `left: 0`, the column with
       * the largest `left` offset is the last (innermost) one; within a
       * sticky-right stack starting at `right: 0`, the column with the
       * largest `right` offset is the innermost one on that side.
       */
      const stickyColumns = useMemo(() => {
        const headerCandidates = (tableData.headers ?? [])
          .filter((h) => h?.isVisible !== false && !h?.isHidden)
          .map((h) => asSxRecord(h?.sx))
          .filter(
            (sx): sx is Record<string, unknown> => sx?.position === "sticky"
          );

        // The hardcoded right-sticky "row menu" column (see render body
        // below) isn't part of `tableData.headers` — include it explicitly
        // when it will actually be rendered.
        const candidates = hasMenuColumn
          ? [
              ...headerCandidates,
              {
                position: "sticky" as const,
                right: 0,
                width: MENU_COLUMN_WIDTH_PX,
              },
            ]
          : headerCandidates;

        let innermostLeft: { offsetPx: number; widthPx: number } | undefined;
        let innermostRight: { offsetPx: number; widthPx: number } | undefined;

        for (const sx of candidates) {
          const widthPx = getEffectiveColumnWidthPx(sx);

          if (sx.left !== undefined) {
            const offsetPx = parseFloat(String(sx.left));
            if (
              !Number.isNaN(offsetPx) &&
              (!innermostLeft || offsetPx > innermostLeft.offsetPx)
            ) {
              innermostLeft = { offsetPx, widthPx };
            }
          }

          if (sx.right !== undefined) {
            const offsetPx = parseFloat(String(sx.right));
            if (
              !Number.isNaN(offsetPx) &&
              (!innermostRight || offsetPx > innermostRight.offsetPx)
            ) {
              innermostRight = { offsetPx, widthPx };
            }
          }
        }

        return { innermostLeft, innermostRight };
        // The hardcoded menu column's width (36) never changes; only its
        // presence (derived from `data`) and the header configs matter.
      }, [tableData.headers, hasMenuColumn]);

      useEffect(() => {
        const tableEl = tableElementRef.current;

        if (!tableEl || typeof ResizeObserver === "undefined") {
          return;
        }

        const updateTableHeight = () => setTableHeightPx(tableEl.offsetHeight);
        updateTableHeight();

        const resizeObserver = new ResizeObserver(updateTableHeight);
        resizeObserver.observe(tableEl);

        return () => resizeObserver.disconnect();
        // Re-measure whenever rows/columns are added/removed/resized.
      }, [tableData]);

      const dragSensor = useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      });

      // TODO: This causes an error about hooks changing between renders in the documentation.
      // For now we can leave this as is, but we should take a look at this again.
      useMemo(() => {
        if (!colOrder || !allowColumnReorder) {
          setTableData({
            headers: tableHeaderEntries,
            components: this.componentsInColumns,
            data,
          });

          return;
        }

        const headerCount = tableHeaderEntries?.length ?? 0;
        const normalizedOrder = [
          ...(colOrder ?? []).filter(
            (index) =>
              Number.isInteger(index) && index >= 0 && index < headerCount
          ),
          ...Array.from({ length: headerCount }, (_, i) => i).filter(
            (index) => !(colOrder ?? []).includes(index)
          ),
        ];

        setTableData({
          headers: tableHeaderEntries
            ? normalizedOrder.map((index) => tableHeaderEntries[index])
            : [],
          components: (this.componentsInColumns
            ? normalizedOrder.map((index) => this.componentsInColumns[index])
            : []) as unknown as Definition,
          data: data.map((entry) => {
            const newColumnProps = [...entry.columnProps];

            return {
              ...entry,
              columnProps: normalizedOrder.map(
                (index) => newColumnProps[index]
              ) as unknown as Definition,
            };
          }),
        });
      }, [tableHeaderEntries, colOrder, data, allowColumnReorder]);

      const moveItem = (array: unknown[], from: number, to: number) => {
        const item = array[from];
        array.splice(from, 1);
        array.splice(to, 0, item);

        return array;
      };

      const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!active || !over) {
          return;
        }

        if (active.id !== over.id) {
          if (!tableData || !tableData.data || !tableData.headers) {
            return;
          }

          const oldIndex = tableData.headers.findIndex(
            (header) => header?.fieldName === active.id
          );
          const newIndex = tableData.headers.findIndex(
            (header) => header?.fieldName === over.id
          );

          // Keep the first column pinned: it cannot be dragged and no column
          // can be moved into the first position.
          if (oldIndex === 0 || newIndex === 0) {
            return;
          }

          if (setColOrder && allowColumnReorder) {
            const order = colOrder
              ? [...colOrder]
              : tableData.headers.map((_, index) => index);

            setColOrder(moveItem(order ?? [], oldIndex, newIndex) as number[]);
            fireEvent?.("reorderColumns", {});
          }
        }
      };
      // state
      const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
      const [menuRowIndex, setMenuRowIndex] = useState<number | null>(null);
      const [menuActions, setMenuActions] = useState<
        PMTableRowProps["onMenuOpen"] | undefined
      >(undefined);
      const [hiddenColumnsAnchorEl, setHiddenColumnsAnchorEl] =
        useState<HTMLElement | null>(null);
      const [addColumnInsertAfter, setAddColumnInsertAfter] = useState<{
        index: number;
        fieldName: string;
      } | null>(null);

      const moveOrderItem = (array: number[], from: number, to: number) => {
        const copy = [...array];
        const [item] = copy.splice(from, 1);
        copy.splice(to, 0, item);
        return copy;
      };

      const ensureFullOrder = (order: number[] | undefined, size: number) => {
        let nextOrder = order
          ? [...order]
          : Array.from({ length: size }, (_, i) => i);

        // Drop out-of-range indices (can happen if header definitions change)
        nextOrder = nextOrder.filter((i) => i >= 0 && i < size);

        // Ensure all indices exist (important when new headers are introduced elsewhere)
        for (let i = 0; i < size; i += 1) {
          if (!nextOrder.includes(i)) {
            nextOrder.push(i);
          }
        }

        return nextOrder;
      };

      const setColumnVisible = (
        headers: (TableHeaderEntry | undefined)[] | undefined,
        fieldName: string,
        isVisible: boolean
      ): TableHeaderEntry[] => {
        const currentHeaders = headers ?? [];
        return currentHeaders
          .filter((h): h is TableHeaderEntry => Boolean(h))
          .map((h) =>
            h.fieldName === fieldName
              ? {
                  ...h,
                  isVisible,
                }
              : h
          );
      };

      const applyInsertAfterToOrder = (
        headers: (TableHeaderEntry | undefined)[],
        order: number[],
        movedFieldName: string,
        insertAfterFieldName: string
      ) => {
        const movedHeaderIndex = headers.findIndex(
          (h) => h?.fieldName === movedFieldName
        );
        const insertAfterHeaderIndex = headers.findIndex(
          (h) => h?.fieldName === insertAfterFieldName
        );

        const fromPos = order.indexOf(movedHeaderIndex);
        const toPosRaw = order.indexOf(insertAfterHeaderIndex);

        if (
          movedHeaderIndex === -1 ||
          insertAfterHeaderIndex === -1 ||
          fromPos === -1 ||
          toPosRaw === -1
        ) {
          return order;
        }

        const desiredToPos = toPosRaw + 1;
        const adjustedToPos =
          desiredToPos > fromPos ? desiredToPos - 1 : desiredToPos;

        if (fromPos === adjustedToPos) {
          return order;
        }

        return moveOrderItem(order, fromPos, adjustedToPos);
      };

      const isHiddenColumnsPopperOpen = Boolean(hiddenColumnsAnchorEl);
      const closeHiddenColumnsPopper = () => {
        setHiddenColumnsAnchorEl(null);
        setAddColumnInsertAfter(null);
      };

      const openMenu = (
        e: React.MouseEvent<HTMLButtonElement>,
        idx: number,
        actions: PMTableRowProps["onMenuOpen"] | undefined
      ) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuRowIndex(idx);
        setMenuActions(actions);
      };

      const closeMenu = () => {
        setMenuAnchor(null);
        setMenuRowIndex(null);
        setMenuActions(undefined);
      };
      return (
        <PMBaseCard
          rightSlotOverride={rightSlotOverride}
          icon={icon}
          fixedHeight={fixedHeight}
          fixedWidth={fixedWidth}
          overflow={overflow}
          title={title}
          showInfoIcon={showInfoIcon}
          showMenuIcon={showMenuIcon}
          style={
            isEditMode
              ? {
                  border: "2px solid #E040FB",
                }
              : undefined
          }
        >
          <Box
            sx={{
              boxShadow: "none",
              maxHeight,
              width: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              flex: 1,
            }}
            ref={tableContainerRef}
          >
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              sensors={[dragSensor]}
            >
              <SortableContext
                items={(
                  tableData?.headers?.filter(
                    (item) => item?.isVisible && !item?.isHidden
                  ) ?? []
                ).map(({ fieldName }) => fieldName)}
                strategy={horizontalListSortingStrategy}
              >
                <TableContainer
                  ref={tableScrollContainerRef}
                  component={Paper}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "auto",
                    paddingTop: isEditMode ? "18px" : undefined,
                    ...tableContainerProps?.sx,
                  }}
                >
                  {/* Rendered first so their static (unstuck) vertical
                      position is the very top of the table — see
                      `StickyColumnShadowOverlay`'s doc comment for why this
                      placement matters. */}
                  {stickyColumns.innermostLeft && (
                    <StickyColumnShadowOverlay
                      side="left"
                      leftPx={stickyColumns.innermostLeft.offsetPx}
                      widthPx={stickyColumns.innermostLeft.widthPx}
                      isVisible={isScrolledFromStart}
                      heightPx={tableHeightPx}
                      transition={stickyShadowTransition}
                      shadowColor={alpha(theme.palette.common.black, 0.25)}
                    />
                  )}
                  {stickyColumns.innermostRight && !hideRightStickyShadow && (
                    <StickyColumnShadowOverlay
                      side="right"
                      // A right-sticky column's marker still needs an
                      // explicit pixel `left` (not the CSS `right` property)
                      // — see `StickyColumnShadowOverlay`'s doc comment for
                      // why. Derive it from the scroll container's visible
                      // `clientWidth` (`containerWidthPx`), not the full
                      // scrollable table width.
                      leftPx={
                        containerWidthPx -
                        stickyColumns.innermostRight.offsetPx -
                        stickyColumns.innermostRight.widthPx
                      }
                      widthPx={stickyColumns.innermostRight.widthPx}
                      isVisible={isScrolledFromEnd}
                      heightPx={tableHeightPx}
                      transition={stickyShadowTransition}
                      shadowColor={alpha(theme.palette.common.black, 0.25)}
                    />
                  )}
                  <Popper
                    open={isHiddenColumnsPopperOpen}
                    anchorEl={hiddenColumnsAnchorEl}
                    placement="bottom-start"
                    sx={{ zIndex: 1300 }}
                  >
                    <ClickAwayListener onClickAway={closeHiddenColumnsPopper}>
                      <Paper
                        sx={{
                          p: 1,
                          border: "1px solid",
                          borderColor: "secondary.main",
                          boxShadow: "none",
                          minWidth: 220,
                        }}
                      >
                        <List dense sx={{ p: 0 }}>
                          {(tableData.headers ?? [])
                            .map((h, index) => ({ h, index }))
                            .filter(
                              ({ h }) => h?.isVisible === false && !h?.isHidden
                            )
                            .map(({ h, index }) => (
                              <ListItem key={h.fieldName} disablePadding>
                                <ListItemButton
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    const nextHeaders = setColumnVisible(
                                      tableHeaderEntries,
                                      h.fieldName,
                                      true
                                    );

                                    // Column ordering is driven by `colOrder` (same as drag & drop).
                                    // When insertAfter is set, update the order to place the shown
                                    // column directly after the selected visible column.
                                    if (setColOrder) {
                                      let nextOrder = ensureFullOrder(
                                        colOrder,
                                        nextHeaders.length
                                      );

                                      if (addColumnInsertAfter?.fieldName) {
                                        nextOrder = applyInsertAfterToOrder(
                                          nextHeaders,
                                          nextOrder,
                                          h.fieldName,
                                          addColumnInsertAfter.fieldName
                                        );
                                      }

                                      setColOrder(nextOrder);
                                    }

                                    fireEvent?.("addColumn", {
                                      column: {
                                        index,
                                        fieldName: h.fieldName,
                                      },
                                      insertAfter:
                                        addColumnInsertAfter ?? undefined,
                                      headers: nextHeaders,
                                    });
                                    closeHiddenColumnsPopper();
                                  }}
                                  sx={{ px: 1, py: 0.25 }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 400 }}
                                  >
                                    {h.label}
                                  </Typography>
                                </ListItemButton>
                              </ListItem>
                            ))}
                        </List>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                  <MuiTable
                    ref={tableElementRef}
                    stickyHeader={isStickyHeader}
                    sx={{ tableLayout: "fixed", width: "100%", ...tableSx }}
                    onMouseLeave={() => {
                      if (!isHiddenColumnsPopperOpen) {
                        setCurrentColumnOnFocus(null);
                      }
                    }}
                  >
                    <PMTableHeader>
                      <PMTableRow>
                        {tableData.headers?.map((header, headerIndex) => {
                          if (header?.isHidden || header?.isVisible === false) {
                            return null;
                          }

                          if (!header) {
                            return null;
                          }

                          const {
                            label,
                            fieldName,
                            sortDirection,
                            isSortable,
                            sx,
                          } = header;

                          const headerSxObject = asSxRecord(sx);
                          const isStickyHeaderColumn =
                            headerSxObject?.position === "sticky";

                          const isColumnFocused =
                            isEditMode && currentColumnOnFocus === headerIndex;

                          return (
                            <PMDraggableTableCell
                              sx={{
                                cursor:
                                  isSortable || allowColumnReorder
                                    ? "pointer"
                                    : "default",
                                minWidth: 150,
                                width: 150,
                                paddingRight: isEditMode ? 6 : undefined,
                                borderTop:
                                  isEditMode &&
                                  currentColumnOnFocus === headerIndex
                                    ? "1px solid #E040FB"
                                    : undefined,
                                borderLeft:
                                  isEditMode &&
                                  currentColumnOnFocus === headerIndex
                                    ? "1px solid #E040FB"
                                    : undefined,
                                borderRight:
                                  isEditMode &&
                                  currentColumnOnFocus === headerIndex
                                    ? "1px solid #E040FB"
                                    : undefined,
                                // Ensure sticky header cells fully cover scrolling columns.
                                backgroundColor:
                                  isStickyHeaderColumn &&
                                  !headerSxObject?.backgroundColor
                                    ? "background.paper"
                                    : undefined,
                                zIndex:
                                  isStickyHeaderColumn &&
                                  !headerSxObject?.zIndex
                                    ? 4
                                    : undefined,
                                ...sx,
                              }}
                              onMouseEnter={() =>
                                setCurrentColumnOnFocus(headerIndex)
                              }
                              key={`${fieldName}-header-${headerIndex}`}
                              fieldName={fieldName}
                              actions={
                                isColumnFocused ? (
                                  <>
                                    <IconButton
                                      size="small"
                                      aria-label="add column"
                                      onPointerDown={(event) => {
                                        event.stopPropagation();
                                      }}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setCurrentColumnOnFocus(headerIndex);
                                        setAddColumnInsertAfter({
                                          index: headerIndex,
                                          fieldName,
                                        });
                                        setHiddenColumnsAnchorEl(
                                          event.currentTarget
                                        );
                                      }}
                                      sx={{
                                        position: "absolute",
                                        right: 1,
                                        top: -18,
                                        zIndex: 1000,
                                        width: 28,
                                        height: 28,
                                        borderRadius: 0,
                                        border: "1px solid #E040FB",
                                        color: "#E040FB",
                                        backgroundColor: "background.paper",
                                        "&:hover": {
                                          backgroundColor: "background.paper",
                                        },
                                      }}
                                    >
                                      <Add sx={{ fontSize: "1rem" }} />
                                    </IconButton>

                                    {headerIndex !== 0 ? (
                                      <IconButton
                                        size="small"
                                        aria-label="delete column"
                                        onPointerDown={(event) => {
                                          event.stopPropagation();
                                        }}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          const nextHeaders = setColumnVisible(
                                            tableHeaderEntries,
                                            fieldName,
                                            false
                                          );
                                          fireEvent?.("deleteColumn", {
                                            column: {
                                              index: headerIndex,
                                              fieldName,
                                            },
                                            headers: nextHeaders,
                                          });
                                        }}
                                        sx={{
                                          position: "absolute",
                                          right: 1,
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                          color: "#E040FB",
                                        }}
                                      >
                                        <Delete sx={{ fontSize: "1.1rem" }} />
                                      </IconButton>
                                    ) : null}
                                  </>
                                ) : null
                              }
                              onClick={() =>
                                fireEvent?.("sort", {
                                  sort: {
                                    key: fieldName,
                                  },
                                })
                              }
                              label={label}
                              isSortable={isSortable}
                              sortDirection={sortDirection}
                              allowColumnReorder={
                                allowColumnReorder && headerIndex !== 0
                              }
                            />
                          );
                        })}
                        {hasMenuColumn && (
                          <PMTableCell
                            size="small"
                            sx={{
                              p: 0,
                              width: MENU_COLUMN_WIDTH_PX,
                              minWidth: MENU_COLUMN_WIDTH_PX,
                              maxWidth: MENU_COLUMN_WIDTH_PX,
                              position: "sticky" as const,
                              right: 0,
                              zIndex: 5,
                              backgroundColor: "background.paper",
                            }}
                          />
                        )}
                      </PMTableRow>
                    </PMTableHeader>
                    <tbody>
                      {tableData.data.map(
                        ({ columnProps, rowProps }, rowIndex) => {
                          const {
                            cardProps,
                            onClick,
                            onEditClick,
                            onDeleteClick,
                            lockMenu,
                            menuTooltip,
                            onMenuOpen,
                          } = rowProps;

                          const MenuIconButton = (
                            <IconButton
                              aria-label="delete table entry"
                              disabled={lockMenu}
                              onClick={(event) => {
                                event.stopPropagation();
                                openMenu(event, rowIndex, onMenuOpen);
                              }}
                            >
                              {lockMenu ? (
                                <Lock sx={{ fontSize: "1.25rem" }} />
                              ) : (
                                <MoreVert sx={{ fontSize: "1.25rem" }} />
                              )}
                            </IconButton>
                          );

                          return (
                            <PMTableRow
                              key={"table-row-" + rowIndex}
                              sx={{
                                cursor: onClick ? "pointer" : "default",
                                ...(cardProps?.sx ?? {}),
                              }}
                              onClick={onClick}
                            >
                              {(columnProps ?? []).map(
                                (
                                  props: {
                                    component?: PimoReactComponent<any, any>;
                                    isHidden?: boolean;
                                    isVisible?: boolean;
                                    cardProps?: {
                                      sx?: SxProps;
                                    };
                                  },
                                  cellIndex
                                ) => {
                                  if (!props) {
                                    return null;
                                  }

                                  const {
                                    component: inlineComponent,
                                    ...columnState
                                  } = props;

                                  const ComponentForColumn =
                                    inlineComponent ??
                                    tableData.components[cellIndex]
                                      ?.component ??
                                    (() => <></>);

                                  const headerForCell =
                                    tableData.headers?.[cellIndex];

                                  const isCellHidden =
                                    columnState?.isHidden ||
                                    columnState?.isVisible === false ||
                                    headerForCell?.isHidden ||
                                    headerForCell?.isVisible === false;

                                  if (isCellHidden) {
                                    return;
                                  }

                                  return (
                                    <PMTableCell
                                      key={`tableRow-${rowIndex}-cell-${cellIndex}`}
                                      onMouseEnter={() =>
                                        setCurrentColumnOnFocus(cellIndex)
                                      }
                                      sx={{
                                        ...(columnState?.cardProps?.sx ?? {}),

                                        borderLeft:
                                          isEditMode &&
                                          currentColumnOnFocus === cellIndex
                                            ? "1px solid #E040FB"
                                            : undefined,
                                        borderRight:
                                          isEditMode &&
                                          currentColumnOnFocus === cellIndex
                                            ? "1px solid #E040FB"
                                            : undefined,
                                      }}
                                    >
                                      <ComponentForColumn
                                        {...columnState}
                                        cardProps={{
                                          ...(columnState?.cardProps ?? {}),
                                          sx: {
                                            padding: 0,
                                            ...(columnState?.cardProps?.sx ??
                                              {}),
                                          },
                                        }}
                                      />
                                    </PMTableCell>
                                  );
                                }
                              )}

                              {onEditClick && (
                                <PMTableCell>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={
                                      getLocalizedMessage(
                                        language,
                                        "editTableEntry"
                                      ) || "edit table entry"
                                    }
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      onEditClick?.(event);
                                    }}
                                  >
                                    <Edit sx={{ fontSize: "1.25rem" }} />
                                  </IconButton>
                                </PMTableCell>
                              )}
                              {onDeleteClick && (
                                <PMTableCell>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    aria-label={
                                      getLocalizedMessage(
                                        language,
                                        "deleteTableEntry"
                                      ) || "delete table entry"
                                    }
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      onDeleteClick?.(event);
                                    }}
                                  >
                                    <Delete sx={{ fontSize: "1.25rem" }} />
                                  </IconButton>
                                </PMTableCell>
                              )}

                              {hasMenuColumn && (
                                <PMTableCell
                                  sx={{
                                    padding: 0,
                                    width: MENU_COLUMN_WIDTH_PX,
                                    minWidth: MENU_COLUMN_WIDTH_PX,
                                    maxWidth: MENU_COLUMN_WIDTH_PX,
                                    position: "sticky" as const,
                                    right: 0,
                                    zIndex: 3,
                                    backgroundColor: "background.paper",
                                  }}
                                >
                                  {onMenuOpen &&
                                    (menuTooltip ? (
                                      <Tooltip
                                        title={menuTooltip}
                                        placement="top"
                                      >
                                        {/* Need to add a span wrapper, because tooltips dont show on disabled buttons */}
                                        <span>{MenuIconButton}</span>
                                      </Tooltip>
                                    ) : (
                                      MenuIconButton
                                    ))}
                                </PMTableCell>
                              )}
                            </PMTableRow>
                          );
                        }
                      )}
                    </tbody>
                  </MuiTable>
                </TableContainer>
              </SortableContext>
            </DndContext>
            <Menu
              id="basic-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
              PaperProps={{
                sx: {
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "secondary.main",
                },
              }}
              slotProps={{
                list: {
                  "aria-labelledby": "basic-button",
                  sx: {
                    boxShadow: "none",
                  },
                },
              }}
            >
              <MenuItem
                disabled={menuActions?.editDisabled ?? false}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!menuActions?.editDisabled && menuRowIndex != null) {
                    menuActions?.onEdit?.(menuRowIndex);
                  }
                  closeMenu();
                }}
              >
                {getLocalizedMessage(language, "edit") || "Edit"}
              </MenuItem>
              <MenuItem
                disabled={menuActions?.deleteDisabled ?? false}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!menuActions?.deleteDisabled && menuRowIndex != null) {
                    menuActions?.onDelete?.(menuRowIndex);
                  }
                  closeMenu();
                }}
              >
                {getLocalizedMessage(language, "delete") || "Delete"}
              </MenuItem>
            </Menu>

            {bottomRightSlotOverride ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  pt: 1,
                }}
              >
                {bottomRightSlotOverride}
              </Box>
            ) : null}
          </Box>
        </PMBaseCard>
      );
    };
  }
}
export default PMTable;
